/**
 * Aadhaar Card OCR Extraction Utility
 * Uses the OCR.space API (free tier) to read text from Aadhaar card
 * images and parse out customer details for auto-filling the form.
 *
 * API key is read from REACT_APP_OCR_SPACE_API_KEY in the frontend .env
 * (get a free key from https://ocr.space/ocrapi/freekey)
 */

import { renderPdfToImages, isPdfFile, PdfPasswordRequiredError } from "./pdfToImages";
import { suggestTitle, splitLeadingSalutation, relationFromText } from "./proposerTitle";

const OCR_API_URL = "https://api.ocr.space/parse/image";
const OCR_API_KEY = (process.env.REACT_APP_OCR_SPACE_API_KEY || "helloworld").trim();

// OpenAI keys start with "sk-" — when one is configured, use OpenAI Vision
// for extraction (more accurate); otherwise use the OCR.space free API
const IS_OPENAI_KEY = OCR_API_KEY.startsWith("sk-");
const OPENAI_API_URL = "https://api.openai.com/v1/chat/completions";

// Rayal_backend AutomationLog API — daily Aadhaar-extraction counter lives
// alongside the captcha-extraction counters (one doc per IST day)
const AUTOMATION_LOG_API = (
  process.env.REACT_APP_AUTOMATION_LOG_API || ""
).trim();

/**
 * Increment today's Aadhaar-extraction counter on the AutomationLog
 * collection (AadharCardExtract field). Same daily-counter API the captcha
 * extraction uses (POST /automationLog/captchaCount). Fire-and-forget:
 * a logging failure must never break the extraction flow.
 */
const logAadharExtractionCount = () => {
  if (!AUTOMATION_LOG_API) return;
  fetch(`${AUTOMATION_LOG_API}/automationLog/captchaCount`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ company: "aadhar", flow: "extract" }),
  }).catch((error) => {
    console.error("Failed to log Aadhaar extraction count:", error.message);
  });
};

/** Read a File into a base64 data URL */
const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read the file"));
    reader.readAsDataURL(file);
  });

/**
 * Send an image/PDF file to OCR.space and get back the raw parsed text
 */
export const extractTextFromImage = async (file) => {
  const formData = new FormData();
  formData.append("apikey", OCR_API_KEY);
  formData.append("file", file);
  formData.append("language", "eng");
  formData.append("isOverlayRequired", "false");
  formData.append("detectOrientation", "true");
  formData.append("scale", "true");
  formData.append("OCREngine", "2"); // Engine 2 is better for cards / mixed fonts

  const response = await fetch(OCR_API_URL, {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`OCR API request failed with status ${response.status}`);
  }

  const result = await response.json();

  if (result.IsErroredOnProcessing) {
    const message = Array.isArray(result.ErrorMessage)
      ? result.ErrorMessage.join(", ")
      : result.ErrorMessage || "OCR processing failed";
    throw new Error(message);
  }

  const parsedText = (result.ParsedResults || [])
    .map((r) => r.ParsedText || "")
    .join("\n");

  if (!parsedText.trim()) {
    throw new Error("No text could be read from the image");
  }

  return parsedText;
};

/** Normalize OCR text into clean, non-empty lines */
const toLines = (text) =>
  text
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+/g, " ").trim())
    .filter(Boolean);

/** Words that are Aadhaar boilerplate, never the customer's name */
const NOISE_WORDS = [
  "government of india",
  "govt of india",
  "unique identification",
  "authority of india",
  "uidai",
  "aadhaar",
  "aadhar",
  "issue date",
  "download date",
  "vid",
  "help@uidai",
  "www.uidai.gov.in",
  "mera aadhaar",
  "meri pehchan",
];

const isNoiseLine = (line) => {
  const lower = line.toLowerCase();
  return NOISE_WORDS.some((w) => lower.includes(w));
};

/** A plausible name: only letters, dots and spaces, at least 3 chars */
const looksLikeName = (line) =>
  /^[A-Za-z][A-Za-z.\s]{2,}$/.test(line) && !isNoiseLine(line);

/**
 * Is this a usable Aadhaar number?
 *
 * Both parsers below strip spaces, so a good read is exactly 12 digits. A
 * partial or smudged scan tends to yield a short run of digits or nothing at
 * all — either way the operator has to re-upload, so anything that is not 12
 * digits is treated as "not read" rather than saved as a broken number.
 *
 * Deliberately NOT a Verhoeff checksum check: the goal is to catch an
 * unreadable IMAGE, and rejecting a correctly-read number because the
 * checksum library disagrees would block a valid policy.
 */
export const isValidAadharNumber = (value) =>
  /^\d{12}$/.test(String(value ?? "").replace(/\s/g, ""));

/**
 * Parse Aadhaar FRONT side text.
 * Returns { fullName, surname, dateOfBirth, gender, aadharNumber }
 * (any field may be null when not found)
 */
export const parseAadharFront = (text) => {
  const lines = toLines(text);
  const result = {
    fullName: null,
    surname: null,
    dateOfBirth: null,
    gender: null,
    aadharNumber: null,
    salutation: null,
  };

  // --- Aadhaar number: 12 digits, usually spaced as 4-4-4 ---
  const aadharMatch = text.match(/\b(\d{4}\s?\d{4}\s?\d{4})\b/);
  if (aadharMatch) {
    result.aadharNumber = aadharMatch[1].replace(/\s/g, "");
  }

  // --- Date of birth: "DOB: 01/01/1990" (also - or . separators) ---
  const dobMatch = text.match(
    /(?:DOB|D\.O\.B|Date of Birth)[^0-9]{0,10}(\d{2}[/\-.]\d{2}[/\-.]\d{4})/i
  );
  let dobLineIndex = -1;
  if (dobMatch) {
    const [dd, mm, yyyy] = dobMatch[1].split(/[/\-.]/);
    const parsed = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    if (!isNaN(parsed.getTime())) {
      result.dateOfBirth = parsed;
    }
    dobLineIndex = lines.findIndex((l) => l.includes(dobMatch[1]));
  } else {
    // Older cards only carry "Year of Birth: 1990"
    const yobMatch = text.match(/Year of Birth[^0-9]{0,10}(\d{4})/i);
    if (yobMatch) {
      result.dateOfBirth = new Date(Number(yobMatch[1]), 0, 1);
      dobLineIndex = lines.findIndex((l) => l.includes(yobMatch[1]));
    }
  }

  // --- Gender ---
  if (/\b(female|mahila)\b/i.test(text)) {
    result.gender = "female";
  } else if (/\b(male|purush)\b/i.test(text)) {
    result.gender = "male";
  } else if (/\btransgender\b/i.test(text)) {
    result.gender = "other";
  }

  // --- Name: the line just above the DOB line is the English name ---
  let nameLine = null;
  if (dobLineIndex > 0) {
    for (let i = dobLineIndex - 1; i >= 0; i--) {
      if (looksLikeName(lines[i])) {
        nameLine = lines[i];
        break;
      }
    }
  }
  // Fallback: first plausible name line on the card
  if (!nameLine) {
    nameLine = lines.find(
      (l) => looksLikeName(l) && !/male|female|dob|birth/i.test(l)
    );
  }

  if (nameLine) {
    // "SHRI RAMESH KUMAR": the salutation is the card's own title, not part of the name
    const named = splitLeadingSalutation(nameLine);
    result.salutation = named.salutation;
    nameLine = named.name;
    const parts = nameLine.toUpperCase().split(/\s+/).filter(Boolean);
    if (parts.length > 1) {
      result.surname = parts[parts.length - 1];
      result.fullName = parts.slice(0, -1).join(" ");
    } else {
      result.fullName = parts[0];
    }
  }

  return result;
};

/** Map of state names (normalized) → form state option values */
const STATE_VALUE_MAP = {
  tamilnadu: "tamilnadu",
  pondicherry: "pondicherry",
  puducherry: "pondicherry",
  karnataka: "karnataka",
  kerala: "kerala",
  maharashtra: "maharashtra",
  delhi: "delhi",
  gujarat: "gujarat",
  rajasthan: "rajasthan",
  westbengal: "westbengal",
  andhrapradesh: "andhrapradesh",
  telangana: "telangana",
};

/**
 * Parse Aadhaar BACK side text (address side).
 * Returns { fatherName, flatDoorNo, roadStreetLane, areaAndLocality,
 *           state, pincode, country }
 */
export const parseAadharBack = (text) => {
  const lines = toLines(text);
  const result = {
    fatherName: null,
    relation: null,
    flatDoorNo: null,
    roadStreetLane: null,
    areaAndLocality: null,
    state: null,
    pincode: null,
    country: "INDIA",
  };

  // --- Pincode: 6 digits ---
  const pinMatch = text.match(/\b([1-9]\d{5})\b/);
  if (pinMatch) {
    result.pincode = pinMatch[1];
  }

  // --- State: match against known state names ---
  const normalizedText = text.toLowerCase().replace(/[^a-z]/g, "");
  for (const [key, value] of Object.entries(STATE_VALUE_MAP)) {
    if (normalizedText.includes(key)) {
      result.state = value;
      break;
    }
  }

  // --- Father/Guardian name from S/O, D/O, W/O, C/O prefix ---
  // The prefix itself (W/O -> Mrs., D/O -> Ms., S/O -> Mr. — see suggestTitle)
  result.relation = relationFromText(text.match(/\b(?:S\/O|D\/O|W\/O|C\/O)/i)?.[0]) || null;
  const relationMatch = text.match(
    /\b(?:S\/O|D\/O|W\/O|C\/O)[\s.:]*([A-Za-z][A-Za-z.\s]{2,40}?)(?=[,\n]|$)/i
  );
  if (relationMatch) {
    result.fatherName = relationMatch[1].trim().toUpperCase();
  }

  // --- Address block: everything after the "Address" keyword ---
  const addressStart = lines.findIndex((l) => /address/i.test(l));
  if (addressStart !== -1) {
    const addressLines = [];
    for (let i = addressStart; i < lines.length; i++) {
      let line = lines[i].replace(/address\s*:?/i, "").trim();
      if (!line || isNoiseLine(line)) continue;
      // Stop once we hit the pincode line (include it, then break)
      addressLines.push(line);
      if (result.pincode && line.includes(result.pincode)) break;
    }

    // Flatten into comma-separated segments and clean them up
    let segments = addressLines
      .join(",")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      // Drop the S/O-type segment (captured separately as fatherName)
      .filter((s) => !/^(S\/O|D\/O|W\/O|C\/O)/i.test(s))
      // Drop segments that are just the pincode / state / country
      .filter((s) => {
        const norm = s.toLowerCase().replace(/[^a-z]/g, "");
        if (result.pincode && s.replace(/\D/g, "") === result.pincode && norm.length === 0)
          return false;
        if (norm === "india") return false;
        if (STATE_VALUE_MAP[norm]) return false;
        return true;
      })
      .map((s) =>
        // Strip trailing "- 600001" style pincode suffixes
        result.pincode ? s.replace(new RegExp(`[-\\s]*${result.pincode}`), "").trim() : s
      )
      .filter(Boolean);

    if (segments.length > 0) {
      result.flatDoorNo = segments[0].toUpperCase();
    }
    if (segments.length > 1) {
      result.roadStreetLane = segments[1].toUpperCase();
    }
    if (segments.length > 2) {
      result.areaAndLocality = segments.slice(2).join(", ").toUpperCase();
    }
  }

  return result;
};

/**
 * The extraction prompt, shared by the single-image and both-sides calls.
 *
 * It was previously duplicated in both functions, so improving one silently
 * left the other behind — and it only asked for four of the form's eight
 * address fields, telling the model to join "area, locality, village, district"
 * into one string. That is why everything landed in a single box.
 *
 * The rules below are deliberately explicit: naming each target field, saying
 * what must NOT be merged, and giving a worked example is what actually makes
 * the model split an address instead of dumping it.
 *
 * 2026-10-28 (the owner: "some aadhaar card have the address on the back side ... fill maximum of field"): the address
 * is looked for on EVERY side given — it used to be told "the back is the address", which steered it away from cards
 * that print it on the front — and it returns the e-Aadhaar parts (post office, sub-district) as well, so
 * distributeAddress has a part for every box the card allows.
 */
export const buildExtractionPrompt = (sideHint) => `${sideHint}

WHERE THE ADDRESS IS: the address can be printed on the FRONT or on the BACK —
older cards print it on the back, newer cards and e-Aadhaar prints often print
it on the front. Look at EVERY side you are given and read the address wherever
it appears. If no address is printed in what you are given, return null for
every address field.

An Aadhaar card prints the address twice — once in a regional language and once
in English. Read the ENGLISH block. Ignore the "Government of India"/UIDAI
headings, the VID line, and any QR code.

Reply with ONLY a JSON object using these exact keys. Use null — never an empty
string, never a guess — for anything you cannot actually read:

{
  "fullName": "given name(s) only, WITHOUT the final surname word",
  "surname": "the final word of the printed name",
  "dateOfBirth": "DD/MM/YYYY",
  "gender": "male | female | other",
  "printedSalutation": "a title PRINTED on the card right before the name — Shri, Smt, Kumari, Mr, Mrs, Miss, Ms — exactly as printed, else null. NEVER infer one from the gender or the name; if the card shows only the name, this is null",
  "aadharNumber": "the 12 digit number, digits only",
  "relationPrefix": "the prefix printed before the guardian's name in the address: exactly one of S/O, D/O, W/O, C/O, else null",
  "fatherName": "the name following S/O, D/O, W/O or C/O (that prefix is NOT part of the name)",
  "flatDoorNo": "house / door / plot number only, e.g. '12/4A' or 'NO 27'",
  "floorNo": "floor if stated, e.g. '2ND FLOOR', else null",
  "buildingName": "apartment / building / house name, e.g. 'GREEN VIEW APARTMENTS', else null",
  "blockName": "block or wing, e.g. 'BLOCK C', else null",
  "roadStreetLane": "street / road / lane / cross / main, e.g. 'GANDHI STREET'",
  "areaAndLocality": "the locality or area name ONLY, e.g. 'ANNA NAGAR'",
  "landmark": "near/opposite landmark if stated, else null",
  "village": "village / town / city name — on e-Aadhaar the value after 'VTC:' — else null",
  "postOffice": "the value after 'PO:' if printed, else null",
  "subDistrict": "the value after 'Sub District:' (taluk / tehsil) if printed, else null",
  "district": "district name — on e-Aadhaar the value after 'District:' — else null",
  "state": "state name in full",
  "pincode": "6 digit PIN, digits only",
  "mobileNumber": "10 digit mobile number if printed, else null",
  "fullAddress": "the complete English address exactly as printed, one line"
}

THE FORM THIS FILLS has six address boxes: Flat/Room No, Floor No, Name of
Premises, Block Name/No, Road/Street/Lane, Area and Locality. Every part you
return in its own key fills a box, so return EVERY printed part of the address,
each in its own key.

SPLITTING RULES — these matter most:
- Put each part of the address in its OWN field. Do NOT copy the whole address
  into areaAndLocality, and do NOT repeat the same text in several fields.
- areaAndLocality is the locality name ALONE. The landmark, village, post
  office, sub-district, district, state and PIN each have their own field and
  must not be appended to it.
- Return only the values, never the labels ("VTC:", "PO:", "District:" ...).
- If a part is genuinely not printed on the card, return null for it rather
  than moving another part into its place.
- Keep fullAddress complete and unsplit — it is the safety net, not a field.

WORKED EXAMPLE 1
Printed: "S/O Ramesh Kumar, 12/4A, 2nd Floor, Green View Apartments, Block C,
Gandhi Street, Anna Nagar, Near Bus Stand, Madurai, Tamil Nadu - 625020"
Correct output:
  fatherName: "Ramesh Kumar", flatDoorNo: "12/4A", floorNo: "2nd Floor",
  buildingName: "Green View Apartments", blockName: "Block C",
  roadStreetLane: "Gandhi Street", areaAndLocality: "Anna Nagar",
  landmark: "Near Bus Stand", district: "Madurai", state: "Tamil Nadu",
  pincode: "625020"

WORKED EXAMPLE 2 (e-Aadhaar style)
Printed: "S/O: Murugan, 2/120, Pillaiyar Kovil Street, Keezhapalur, VTC: Keezhapalur,
PO: Keezhapalur, Sub District: Ariyalur, District: Ariyalur, State: Tamil Nadu,
PIN Code: 621707"
Correct output:
  fatherName: "Murugan", flatDoorNo: "2/120", floorNo: null, buildingName: null,
  blockName: null, roadStreetLane: "Pillaiyar Kovil Street",
  areaAndLocality: "Keezhapalur", landmark: null, village: "Keezhapalur",
  postOffice: "Keezhapalur", subDistrict: "Ariyalur", district: "Ariyalur",
  state: "Tamil Nadu", pincode: "621707"`;

// What the parts of an Indian address look like. Used to place a part that arrived in the wrong box (or in one
// crammed line) into the box it belongs to.
//
// A door number is a SHORT token — "12/4A", "No 27", "Plot 8". Requiring the whole part to be a number (after an
// optional door-ish prefix) is what stops "45 MG Road" being swallowed as a door number and losing the street.
const DOOR_RE = /^(?:(?:door|flat|plot|house|[dh]|no|#)\.?\s*)*\d+[\w/-]*$/i;
const STREET_RE = /\b(street|road|lane|cross|main|marg|avenue|st|rd|salai|veedhi|veethi|gali)\b/i;
const FLOOR_RE = /\b(floor|flr)\b/i;
const BUILDING_RE =
  /\b(apartments?|flats?|towers?|villa|bhavan|complex|residency|enclave|nilayam|illam|niwas|nivas|sadan|mansion|heights|plaza|building|bldg|chambers)\b/i;
const BLOCK_RE = /\b(block|blk|wing|phase|sector)\b/i;
const LANDMARK_RE = /\b(near|nr|opp|opposite|behind|beside)\b/i;
const PIN_RE = /^[1-9]\d{5}$/;
const RELATION_RE = /^(s\/o|d\/o|w\/o|c\/o)\b/i;
// The labels an e-Aadhaar prints before a value. Only taken off when followed by a colon, so "POONAMALLEE" keeps its
// "PO" and "DISTRICT HEAD QUARTERS" keeps its first word.
const LABEL_RE =
  /^(?:address|vtc|po|p\.o\.?|post\s*office|sub[\s-]*district|district|dist\.?|taluk|tehsil|landmark|state|pin\s*code|pincode)\s*:-?\s*/i;

// A box value the form can take: a door number has a digit or a door word, a floor says FLOOR or is a number / an
// ordinal. Anything else in those two boxes is a place name in the wrong box.
const looksLikeDoor = (v) => /\d/.test(v) || /^(?:no|plot|door|flat|house|h\.?\s*no|d\.?\s*no|#)\b/i.test(v);
const looksLikeFloor = (v) =>
  FLOOR_RE.test(v) ||
  /^\d+$/.test(v) ||
  /\b\d+\s*(st|nd|rd|th)\b/i.test(v) ||
  /^(ground|first|second|third|fourth|fifth|g|gf)\b/i.test(v);

// For comparing parts: case, spaces and punctuation do not make two parts different.
const sameText = (v) => String(v || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/**
 * One address part, cleaned: labels and a trailing PIN off, spaces collapsed, uppercase. Null when nothing is left,
 * or when the part is one the form keeps elsewhere — the S/O name, the state, the PIN, the country.
 */
const cleanAddressPart = (value, state) => {
  if (typeof value !== "string") return null;
  let v = value.replace(/\s+/g, " ").trim();
  // "District: Ariyalur" -> "Ariyalur" (twice: "Address: VTC: X")
  v = v.replace(LABEL_RE, "").replace(LABEL_RE, "").trim();
  // A trailing "- 625020" belongs to the pincode field, not the address.
  v = v.replace(/[-–]?\s*[1-9]\d{5}\s*$/, "").replace(/^[,\s]+|[,\s-]+$/g, "").trim();
  if (!v || v.toLowerCase() === "null") return null;
  if (RELATION_RE.test(v) || PIN_RE.test(v)) return null;
  const letters = v.toLowerCase().replace(/[^a-z]/g, "");
  if (letters === "india" || STATE_VALUE_MAP[letters]) return null;
  if (state && letters && letters === String(state).toLowerCase().replace(/[^a-z]/g, "")) return null;
  return v.toUpperCase();
};

/** "A, B , C" -> ["A", "B", "C"], each cleaned. */
const splitParts = (line, state) =>
  String(line || "")
    .split(/[,\n]/)
    .map((p) => cleanAddressPart(p, state))
    .filter(Boolean);

const ADDRESS_BOXES = ["flatDoorNo", "floorNo", "buildingName", "blockName", "roadStreetLane", "areaAndLocality"];

/**
 * Put the address into as many of the form's six boxes as the card allows (2026-10-28, chosen by the owner):
 *
 *   1. every part goes to its own box — the AI's labels first, then the shape of the part (a door number, "street",
 *      "apartments", "block" ...) for a part that arrived in the wrong box or in one crammed line;
 *   2. the parts the form has no box for — landmark, village, post office, sub-district, district — fill the EMPTY
 *      Name of Premises box, then the EMPTY Block Name/No box, in the order they are printed;
 *   3. only what is still left joins Area and Locality.
 *
 * Flat/Room No and Floor No never get a place name: a floor the card does not print stays empty for the operator,
 * because a made-up one would be a false address on the policy. Area is never left empty while the card has a
 * place name to put there.
 *
 * @param {object} labelled - the AI's keys: the six boxes plus landmark, village, postOffice, subDistrict, district
 * @param {{fullAddress?: string, state?: string}} [opts] - the printed line (the fallback, and the printed order)
 * @returns {{flatDoorNo, floorNo, buildingName, blockName, roadStreetLane, areaAndLocality}} uppercase or null
 */
export const distributeAddress = (labelled = {}, { fullAddress = null, state = null } = {}) => {
  const boxes = {};
  for (const key of ADDRESS_BOXES) boxes[key] = null;
  // Parts with no box yet, in the order they turn up. `extra` = a part the form has no box for.
  const loose = [];
  const extras = [];

  const given = {};
  for (const key of ADDRESS_BOXES) given[key] = splitParts(labelled[key], state);
  const nothingSplit = ADDRESS_BOXES.every((key) => given[key].length === 0);

  if (nothingSplit) {
    // The model returned no split at all — split the printed line here rather than leave the form empty.
    loose.push(...splitParts(fullAddress, state));
  } else {
    // Area holds the locality ALONE: its parts are placed one by one, and they go FIRST, so the part the model read
    // as the locality is the one that gets the Area box.
    loose.push(...given.areaAndLocality);
    for (const key of ADDRESS_BOXES) {
      const parts = given[key];
      if (key === "areaAndLocality" || !parts.length) continue;
      if (parts.length >= 3) {
        // Three parts in one box is the whole address dumped into it: take the box apart too.
        loose.push(...parts);
      } else {
        boxes[key] = parts.join(", ");
      }
    }
  }

  // The two boxes that only take a number-like value: a place name found there is placed like any other part.
  if (boxes.flatDoorNo && !looksLikeDoor(boxes.flatDoorNo)) {
    loose.push(boxes.flatDoorNo);
    boxes.flatDoorNo = null;
  }
  if (boxes.floorNo && !looksLikeFloor(boxes.floorNo)) {
    loose.push(boxes.floorNo);
    boxes.floorNo = null;
  }

  const isTaken = (part) =>
    ADDRESS_BOXES.some((key) => boxes[key] && sameText(boxes[key]) === sameText(part)) ||
    extras.some((e) => sameText(e.text) === sameText(part));

  // A part's own box, by its shape — only a box that is still empty.
  const SHAPES = [
    ["flatDoorNo", (p) => DOOR_RE.test(p) && !STREET_RE.test(p)],
    ["floorNo", (p) => FLOOR_RE.test(p)],
    ["buildingName", (p) => BUILDING_RE.test(p)],
    ["blockName", (p) => BLOCK_RE.test(p)],
    ["roadStreetLane", (p) => STREET_RE.test(p)],
  ];
  for (const part of loose) {
    if (isTaken(part)) continue;
    if (LANDMARK_RE.test(part)) {
      extras.push({ text: part, landmark: true });
      continue;
    }
    const shape = SHAPES.find(([key, test]) => !boxes[key] && test(part));
    if (shape) {
      boxes[shape[0]] = part;
    } else if (!boxes.areaAndLocality) {
      boxes.areaAndLocality = part;
    } else {
      extras.push({ text: part });
    }
  }

  // The parts the AI labelled that the form has no box of its own for.
  for (const key of ["landmark", "village", "postOffice", "subDistrict", "district"]) {
    const part = cleanAddressPart(labelled[key], state);
    if (part && !isTaken(part)) extras.push({ text: part, landmark: key === "landmark" });
  }

  // Printed order: an address runs from the most specific part to the broadest, so the extras keep that order.
  // A part that cannot be found in the printed line keeps its place behind the ones that can (the sort is stable).
  const printed = sameText(fullAddress);
  const at = (text) => {
    const i = printed ? printed.indexOf(sameText(text)) : -1;
    return i === -1 ? Infinity : i;
  };
  extras.sort((a, b) => at(a.text) - at(b.text));

  // Area empty: the first place name (not a landmark) is the locality.
  if (!boxes.areaAndLocality) {
    const i = extras.findIndex((e) => !e.landmark);
    if (i !== -1) boxes.areaAndLocality = extras.splice(i, 1)[0].text;
  }
  if (!boxes.buildingName && extras.length) boxes.buildingName = extras.shift().text;
  if (!boxes.blockName && extras.length) boxes.blockName = extras.shift().text;
  if (extras.length) {
    boxes.areaAndLocality = [boxes.areaAndLocality, ...extras.map((e) => e.text)].filter(Boolean).join(", ");
  }

  return boxes;
};

/** Did the read find any part of an address at all? (A single upload of the side without one finds none.) */
export const hasAnyAddress = (data = {}) =>
  ADDRESS_BOXES.some((key) => Boolean(data[key])) || Boolean(data.pincode);

/** Normalize the JSON returned by OpenAI Vision into form-ready values */
export const normalizeAiResult = (raw, side) => {
  const clean = (v) =>
    typeof v === "string" && v.trim() && v.trim().toLowerCase() !== "null"
      ? v.trim()
      : null;

  const result = {};
  // "merged" = both sides of the card in ONE picture: it carries everything "both" does.
  const readsFront = side === "front" || side === "both" || side === "merged";
  const readsBack = side === "back" || side === "both" || side === "merged";

  if (readsFront) {
    // A salutation the model left glued to the name ("SHRI RAMESH") is split off, so it never lands in the Full Name box
    // and still counts as the card's own title.
    const named = splitLeadingSalutation(clean(raw.fullName) || "");
    result.fullName = named.name ? named.name.toUpperCase() : null;
    result.surname = clean(raw.surname)?.toUpperCase() || null;
    // Only what is PRINTED: the title is worked out from it (and the relation / gender) in suggestTitle, not guessed here.
    result.salutation = clean(raw.printedSalutation) || named.salutation || null;
    result.aadharNumber = clean(raw.aadharNumber)?.replace(/\s/g, "") || null;

    // Date: expect DD/MM/YYYY (also tolerate - or . separators)
    result.dateOfBirth = null;
    const dobStr = clean(raw.dateOfBirth);
    if (dobStr) {
      const m = dobStr.match(/(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/);
      if (m) {
        const parsed = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
        if (!isNaN(parsed.getTime())) result.dateOfBirth = parsed;
      }
    }

    const gender = clean(raw.gender)?.toLowerCase();
    result.gender =
      gender === "male" || gender === "female"
        ? gender
        : gender
          ? "other"
          : null;
  }

  if (readsBack) {
    const upper = (v) => clean(v)?.toUpperCase() || null;

    result.fatherName = upper(raw.fatherName);
    // S/O | D/O | W/O | C/O as printed, "" -> null. Used for the title: W/O -> Mrs., D/O -> Ms., S/O -> Mr.
    result.relation = relationFromText(raw.relationPrefix) || relationFromText(raw.fullAddress) || null;

    // The six address boxes, filled as far as the card allows (see distributeAddress). The model's own split is the
    // first source; the printed line is the fallback when it split nothing and the order the extras keep.
    Object.assign(
      result,
      distributeAddress(raw, { fullAddress: clean(raw.fullAddress), state: clean(raw.state) })
    );

    result.pincode = clean(raw.pincode)?.replace(/\D/g, "") || null;
    result.country = "INDIA";

    // Only accept a plausible Indian mobile number.
    const mobile = clean(raw.mobileNumber)?.replace(/\D/g, "") || "";
    const mobile10 = mobile.length > 10 ? mobile.slice(-10) : mobile;
    result.mobileNumber = /^[6-9]\d{9}$/.test(mobile10) ? mobile10 : null;

    // Map the state name onto the form's option values
    const stateNorm = clean(raw.state)?.toLowerCase().replace(/[^a-z]/g, "");
    result.state = (stateNorm && STATE_VALUE_MAP[stateNorm]) || null;
  }

  return result;
};

/**
 * Call the OpenAI Vision API with one or more page images.
 *
 * Takes data URLs rather than Files so the SAME call serves photos and PDFs —
 * a PDF is rasterised to page images first (utils/pdfToImages.js) and then
 * follows exactly this path, instead of dropping to the much weaker OCR.space
 * text fallback.
 */
const callVisionApi = async (dataUrls, prompt, side) => {
  const response = await fetch(OPENAI_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${OCR_API_KEY}`,
    },
    body: JSON.stringify({
      model: "gpt-4o-mini",
      response_format: { type: "json_object" },
      // Reading printed text is not a creative task — 0 keeps the same card
      // returning the same answer instead of varying between runs.
      temperature: 0,
      // The richer schema needs the room; 500 truncated longer addresses,
      // which produced invalid JSON and looked like "extraction failed".
      max_tokens: 1200,
      messages: [
        {
          role: "user",
          content: [
            { type: "text", text: prompt },
            // detail:"high" stops the API downscaling the image before reading.
            // Aadhaar address text is small — this is the single biggest
            // accuracy win, and without it fields were simply misread.
            ...dataUrls.map((url) => ({
              type: "image_url",
              image_url: { url, detail: "high" },
            })),
          ],
        },
      ],
    }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(
      result?.error?.message || `Extraction API failed with status ${response.status}`
    );
  }

  const content = result?.choices?.[0]?.message?.content;
  if (!content) {
    throw new Error("No data returned by the extraction API");
  }

  return normalizeAiResult(JSON.parse(content), side);
};

/**
 * Turn one uploaded file into the page images to send.
 *
 * A PDF becomes one image per page; anything else is used as-is.
 */
const fileToVisionImages = async (file, pdfPassword) => {
  if (isPdfFile(file)) {
    return renderPdfToImages(file, { password: pdfPassword });
  }
  return [await fileToDataUrl(file)];
};

const sideHintFor = (side) =>
  side === "front"
    ? "This is the FRONT side of an Aadhaar card (photo, name, DOB, gender, Aadhaar number)."
    : side === "back"
      ? "This is the BACK side of an Aadhaar card (address side)."
      : side === "merged"
        ? "This ONE picture shows BOTH sides of the SAME Aadhaar card side by side (one side on the left, the other " +
          "on the right). Read both halves and combine everything into ONE result."
        : "This picture shows an Aadhaar card — the front, the back, or both. Combine everything you can read into ONE result.";

/** Extract Aadhaar data from an image or PDF using the OpenAI Vision API */
const extractViaOpenAI = async (file, side, pdfPassword) => {
  const images = await fileToVisionImages(file, pdfPassword);
  // A single-file PDF usually holds BOTH sides, so read it as "both" rather
  // than trusting the side label the upload box guessed.
  const effectiveSide = images.length > 1 ? "both" : side;
  return callVisionApi(
    images,
    buildExtractionPrompt(sideHintFor(effectiveSide)),
    effectiveSide
  );
};

/**
 * Extract from BOTH Aadhaar sides in ONE request.
 *
 * The OpenAI Vision API accepts several images in a single message, so the
 * front and back go up together and come back as one combined result. Doing
 * this as two calls (the previous behaviour) cost twice as much, counted twice
 * against the daily extraction log, and could return two halves that disagree
 * — the model could not cross-check the name on the front against the address
 * block on the back.
 */
const extractBothSidesViaOpenAI = async (frontFile, backFile, pdfPassword) => {
  // Either side may be a PDF, and a PDF may itself render to several pages —
  // flatten everything into one ordered list of images for the single request.
  const [frontImages, backImages] = await Promise.all([
    fileToVisionImages(frontFile, pdfPassword),
    fileToVisionImages(backFile, pdfPassword),
  ]);

  // Not "the LAST image(s) are the address": some cards print the address on the front, and the model took that
  // sentence to mean it need not look there.
  const prompt = buildExtractionPrompt(
    "You are given images of the SAME Aadhaar card: the FIRST image(s) are the " +
    "FRONT and the LAST image(s) are the BACK. Read ALL of them and combine " +
    "them into one result."
  );

  return callVisionApi([...frontImages, ...backImages], prompt, "both");
};

/**
 * Extract Aadhaar data from a front image and a back image in a SINGLE call.
 *
 * Falls back to reading each side separately when the combined path is not
 * available (a PDF, or an OCR.space key instead of an OpenAI one).
 */
export const extractAadharDataFromBothSides = async (
  frontFile,
  backFile,
  pdfPassword
) => {
  // One extraction event, so the daily counter matches what the user did.
  logAadharExtractionCount();

  // PDFs used to be excluded here and fell through to the far weaker
  // OCR.space text path. They are now rasterised to page images first, so an
  // Aadhaar PDF gets the same AI extraction a photo does.
  if (IS_OPENAI_KEY) {
    return extractBothSidesViaOpenAI(frontFile, backFile, pdfPassword);
  }

  // Fallback: OCR.space (or PDFs) can only handle one file per request.
  const [frontText, backText] = await Promise.all([
    extractTextFromImage(frontFile),
    extractTextFromImage(backFile),
  ]);
  return { ...parseAadharFront(frontText), ...parseAadharBack(backText) };
};

/**
 * Convenience: extract Aadhaar data from a file for the front, back, or both
 * (merged/single) side. Uses OpenAI Vision when an sk- key is configured
 * (images only), otherwise the OCR.space text OCR + regex parsing.
 * Returns the parsed field object.
 */
export const extractAadharData = async (file, side = "both", pdfPassword) => {
  // Count every extraction attempt in the AutomationLog daily counter,
  // exactly like the captcha AI-extraction counters
  logAadharExtractionCount();

  // OpenAI Vision path. PDFs are rendered to page images first (see
  // fileToVisionImages), so they no longer drop to the OCR.space fallback.
  if (IS_OPENAI_KEY) {
    return extractViaOpenAI(file, side, pdfPassword);
  }

  const text = await extractTextFromImage(file);
  if (side === "front") return parseAadharFront(text);
  if (side === "back") return parseAadharBack(text);
  return { ...parseAadharFront(text), ...parseAadharBack(text) };
};

// Re-exported so the upload UI can tell "needs a password" apart from a
// genuine extraction failure and prompt for it.
export { PdfPasswordRequiredError };
